/* eslint no-cond-assign: 0 */
import {
    call,
    fork,
    put,
    take,
    select
} from 'redux-saga/effects';

import {
    ERR_SIZE_TYPE_INVALID,
    ERR_QUANTITY_UNAVAIL
} from '../../../core/js/enums/localizationEnums';

import {
    CHANGE_QUANTITY,
    CHANGE_SIZE_TYPE,
    CHANGE_SIZE,
    RESET_SELECTED_SKU,
    SELECT_COLOR_SWATCH,
    canAddToList,
    changeColor,
    resetSize,
    resetQuantity,
    updateSelectedSku,
    updateProductMeta,
    hideProductActionError,
    hideCannotAddToListError,
    updateBopisStockInfo
} from '../actions/productActions';

import {
    getProductsRelatedByColorSaga
} from './shopTheLookSagas';

import { debug, error } from '../../../core/js/utils/logger';

import productUtils from '../utils/utils';
import { isQuickshop } from '../utils/quickshopUtils';
import { parse, stringify } from 'query-string';

export function *changeColorSaga() {
    let payload = {};
    let action;

    while (action = yield take(SELECT_COLOR_SWATCH)) {
        let component;
        let state = yield select();

        payload = action.payload;
        const isForFamilyProduct = productUtils.actionIsForFamilyProduct(action);
        const product = productUtils.getProductForAction(state, action);
        const selectedPrimarySliceItem = productUtils.getSelectedSliceItem(
            product.skuInfo.primarySlice.sliceItems,
            product.skuInfo.primarySlice.sliceHashToIndex.code,
            payload.colorCode
        );
        const imageId = productUtils.getSelectedImageId(selectedPrimarySliceItem);

        if (isForFamilyProduct) {
            component = action.meta.component;
        }

        if (!isQuickshop(state) && !isForFamilyProduct) {
            yield fork(
                getProductsRelatedByColorSaga,
                payload.colorCode,
                productUtils.getInventoryPool()
            );
        }
        yield put(changeColor(selectedPrimarySliceItem.displayName, imageId, payload, component));
    }
}

// Updates the URL (without a page reload) based on the selected values
export function *updateProductUrlSaga(action, queryParams) {
    let state = yield select();

    if (isQuickshop(state)) {
        const product = productUtils.getProductForAction(state);
        const oldQuickshopUrl = product.productMeta.url;
        const newQuickshopUrl = productUtils.updateQuickshopUrl(oldQuickshopUrl, queryParams);

        yield put(updateProductMeta({
            url: newQuickshopUrl,
            queryParams: stringify(queryParams)
        }));
    } else {
        let url;
        const product = productUtils.getProductForAction(state, action);

        if (productUtils.actionIsForFamilyProduct(action)) {
            const updatedQueryParams = productUtils.getUpdatedQueryParams(product, queryParams);
            const updatedQueryString = stringify(updatedQueryParams);
            const component = action.meta.component;

            // Set new URL for View Full Details link by chopping the old query string off
            // and appending the new selections as a new query string
            const productUrl = `${product.productMeta.url.split('?')[0]}?`;

            url = productUrl + updatedQueryString;

            yield put(updateProductMeta({
                url: url,
                queryParams: updatedQueryString
            }, component));
        } else {
            url = productUtils.updateSelectedQueryParams(product, queryParams);
            yield put(updateProductMeta({
                url: url,
                queryParams: stringify(queryParams)
            }));
        }
    }
}

export function *changeQuantitySaga() {
    let action;
    let controls;
    let component;

    while (action = yield take(CHANGE_QUANTITY)) {
        const isForFamilyProduct = productUtils.actionIsForFamilyProduct(action);
        let state = yield select();

        if (isForFamilyProduct) {
            component = action.meta.component;
        }

        const product = productUtils.getProductForAction(state, action);

        controls = product.productControls;

        // Hide errors if they exist already.
        if (product.productMessages && product.productMessages.error) {
            yield put(hideProductActionError(component));
        }
        if (product.productListMessages && product.productListMessages.error) {
            yield put(hideCannotAddToListError(component));
        }

        yield call(updateProductUrlSaga,
            action,
            {
                color: controls.colorCode,
                quantity: controls.quantity,
                size: controls.sizeName,
                type: controls.sizeTypeCode
            }
        );

        const skuObj = _.get(product, 'selectedSku.sku');
        if (skuObj) {
            const bopisStockInfo = productUtils.getBopisStockInfo(state, skuObj);
            yield put(updateBopisStockInfo(bopisStockInfo));
        }
    }
}

/* eslint-disable complexity */
export function *selectedSkuSaga() {
    let payload = {};
    let action;

    const actions = [RESET_SELECTED_SKU, SELECT_COLOR_SWATCH, CHANGE_SIZE_TYPE, CHANGE_SIZE];

    while (action = yield take(actions)) {
        payload = action.payload;

        let component;

        if (productUtils.actionIsForFamilyProduct(action)) {
            component = action.meta.component;
        }

        let state = yield select();

        const product = productUtils.getProductForAction(state, action);
        const backInStockEnabled = _.get(product, 'backInStockEnabled');
        const primarySliceItems = product.skuInfo.primarySlice.sliceItems;
        const primarySliceHashToIndex = product.skuInfo.primarySlice.sliceHashToIndex.code;
        const secondarySliceItems = product.skuInfo.secondarySlice.sliceItems;
        const secondarySliceHashToIndex = product.skuInfo.secondarySlice.sliceHashToIndex.code;
        const controls = product.productControls;
        const selColorCode = payload.colorCode || controls.colorCode;
        const selectedPrimarySliceItem = productUtils.getSelectedSliceItem(primarySliceItems, primarySliceHashToIndex, selColorCode);

        const selColorName = _.get(selectedPrimarySliceItem, 'displayName', '');
        const newMessages = {};

        let selSizeTypeCode = payload.sizeTypeCode || controls.sizeTypeCode;

        const selectedSecondarySliceItem = productUtils.getSelectedSliceItem(secondarySliceItems, secondarySliceHashToIndex, selSizeTypeCode);

        let selSize = payload.sizeName || controls.sizeName;
        let selSizeId = productUtils.getSizeIdFromName(selectedSecondarySliceItem, selSize);
        let selQuantity = controls.quantity;
        let skuObj = null;

        let backInStockEligible = productUtils.isBackInStockEligible(state, selColorCode, selSizeTypeCode, selSizeId, product);

        // Hide errors if they exist already.
        if (product.productMessages && product.productMessages.error) {
            yield put(hideProductActionError(component));
        }
        if (product.productListMessages && product.productListMessages.error) {
            yield put(hideCannotAddToListError(component));
        }

        // By default, set flatRateCode to first available in skus
        newMessages.flatRateCode = productUtils.getDefaultFlatRateCode(primarySliceItems);

        if (selColorCode && selSizeTypeCode && !selSize) {
            const fitInStock = _.get(product, `controlsData.colorData.${selColorCode}.fitData.${selSizeTypeCode}.inStock`);
            newMessages.error = productUtils.setColorFitLevelErrorMessages(payload, fitInStock);
        }

        if (selColorCode && selSizeTypeCode && selSize) {
            skuObj = productUtils.getSkuObj(selectedPrimarySliceItem, selectedSecondarySliceItem, selColorCode, selSizeTypeCode, selSizeId, backInStockEnabled);
            const sizeInColor = backInStockEnabled ? _.get(product, `controlsData.colorData[${selColorCode}].fitData[${selSizeTypeCode}].sizeData[${selSizeId}].inColor`) : true;
            const sizeInStock = backInStockEnabled ? _.get(product, `controlsData.colorData[${selColorCode}].fitData[${selSizeTypeCode}].sizeData[${selSizeId}].inStock`) : true;

            if (!skuObj || !sizeInStock || !sizeInColor) {
                const fitInStock = backInStockEnabled ? _.get(product, `controlsData.colorData.${selColorCode}.fitData.${selSizeTypeCode}.inStock`) : true;

                newMessages.error = productUtils.setSkuErrorMessages(payload, fitInStock, sizeInColor);
            }

            // clear out selected size if no skuObj is returned
            if (!skuObj) {
                // Reset selections
                selSizeId = null;
                selSize = null;
                yield put(canAddToList(false, component));
                yield put(resetSize(selSizeId, selSize, component));
            } else {
                // If we ended up with a sku object but no sizeId
                // or a different sizeId, reset it to the skuObj's
                // and check for BIS eligibility again
                if (!selSizeId || (backInStockEnabled && skuObj.sizeId !== selSizeId)) {
                    selSizeId = skuObj.sizeId;
                    backInStockEligible = productUtils.isBackInStockEligible(state, selColorCode, selSizeTypeCode, selSizeId, product);
                }

                if (skuObj.isFlatRate) {
                    // if the new sku is flatrate, set flatRateCode
                    newMessages.flatRateCode =
                        skuObj.shipRestriction !== null ? skuObj.shipRestriction.codeName : null;
                } else {
                    // else reset flatRateCode
                    newMessages.flatRateCode = null;
                }

                // check skuObj for available inventory
                if (!productUtils.isQuantityValid(skuObj, controls.quantity) && productUtils.getTotalStock(skuObj) > 0) {
                    // enum in contentful error localization container
                    newMessages.error = ERR_QUANTITY_UNAVAIL;

                    // set selected quantity to number of available products
                    selQuantity = productUtils.getTotalStock(skuObj);

                    yield put(resetQuantity(selQuantity, component));
                }

                // tell add to cart and add to wish list buttons to be active
                yield put(canAddToList(true, component));
            }
        } else {
            debug('Not enough selections were made in order to try to get a sku obj');
        }

        if (payload.colorCode) {
            // If backInStockEnabled check for size type validity in new data structure
            // Otherwise go through normal validity check against the size type
            if (backInStockEnabled && !_.get(product, `controlsData.colorData.${selColorCode}.fitData.${selSizeTypeCode}.inColor`)) {
                selSizeTypeCode = productUtils.getDefaultSizeType(secondarySliceItems, selColorCode);
                const sizeInStock = _.get(product, `controlsData.colorData[${selColorCode}].fitData[${selSizeTypeCode}].sizeData[${selSizeId}].inStock`);

                if (sizeInStock && newMessages.error) {
                    newMessages.error = '';
                }
            } else if (!productUtils.isSizeTypeValid(selectedSecondarySliceItem, selColorCode) && !backInStockEnabled) {
                selSizeTypeCode = productUtils.getDefaultSizeType(secondarySliceItems, selColorCode);
                newMessages.error = ERR_SIZE_TYPE_INVALID;
            }
        }

        yield call(updateProductUrlSaga,
            action,
            {
                color: selColorCode,
                type: selSizeTypeCode,
                size: selSize,
                quantity: selQuantity
            }
        );

        // udpate total stock in controls store for quantity logic
        const totalStock = productUtils.getTotalStock(skuObj);

        // get new price from skuObj
        const price = productUtils.getSelectedPrice(skuObj, product.skuInfo);

        state = yield select();

        // get availability message from skuObj
        const availabilityInfo = productUtils.getAvailabilityInfo(state, skuObj);
        const hideListAddLinks = availabilityInfo ? _.get(availabilityInfo, 'isOutOfStock', false) : false;

        const availabilityInfoToBopisMessageKeys = {
            slug: 'availabilityMessage',
            params: 'availabilityMessageParams',
            date: 'availabilityDate'
        };

        const bopisNewMessages = _.map(
            _.get(availabilityInfo, 'messages', []),
            (availabilityItem) => _.mapKeys(
                availabilityItem,
                (k, v) => availabilityInfoToBopisMessageKeys[v]
            )
        );

        const bopisNewStock = productUtils.getBopisStockInfo(state, skuObj);

        const hasVideo = productUtils.doesSelectedSliceItemHaveVideo(selectedPrimarySliceItem);

        // update selected skuObj to new skuObj even it is is empty
        yield put(updateSelectedSku(
            skuObj,
            selSizeTypeCode,
            selSizeId,
            selSize,
            selColorCode,
            selColorName,
            totalStock,
            price,
            newMessages,
            bopisNewMessages,
            bopisNewStock,
            availabilityInfo,
            component,
            hasVideo,
            backInStockEligible,
            hideListAddLinks
        ));
    }
}
/* eslint-enable complexity */

export default [
    changeColorSaga,
    changeQuantitySaga,
    selectedSkuSaga
];
